// Single source of truth for React Query keys — the shared RQ pattern.
//
// Why this exists: keys used to be inline string arrays at ~30 call sites, which
// drifted (courts was cached under both ['courts', {pageSize}] and
// ['courts', {page, pageSize}]; notification actings under both kebab and camel
// case) and made invalidation guesswork. Centralizing them here kills the drift
// and lets a mutation invalidate one prefix to refetch every dependent list.
//
// Two rules:
//   1. Keys are hierarchical. RQ invalidates by PREFIX, so invalidating
//      qk.cases.all() (['cases']) refetches every ['cases', params] entry.
//   2. organizationId stays INSIDE the key where the caller puts it. It is
//      load-bearing, NOT redundant: keying two orgs to the same entry served
//      stale rows across an org switch (see useLedgerQuery). This module only
//      fixes the prefix; callers pass their own param object verbatim.
export const qk = {
  // -- Causas --
  cases: {
    all: () => ['cases'],
    list: (params) => ['cases', params],
    pick: (term) => ['cases-pick', term],
  },
  caseDetail: (caseId) => ['case', caseId],

  // -- Diligencias / Tasks --
  tasks: {
    all: () => ['tasks'],
    list: (params) => ['tasks', params],
    activeLane: (params) => ['tasks-active', params],
    historial: (params) => ['tasks-hist', params],
    recentComunas: () => ['tasks-recent-comunas'],
  },
  taskDetail: (taskId) => ['task', taskId],
  activeTask: {
    all: () => ['active-task'],
    byCase: (caseId) => ['active-task', { caseId }],
  },
  activeDiligencias: (params) =>
    params ? ['active-diligencias', params] : ['active-diligencias'],
  laneCounts: (params) => ['lane-counts', params],

  // -- Quotes / Payments --
  quotes: {
    all: () => ['quotes'],
    byTask: (taskId) => ['quotes', { taskId }],
    byTasks: (taskIds) => ['quotes', { taskIds }],
    inbox: (params) => ['quotes', 'inbox', params],
    answered: (params) => ['quotes', 'answered', params],
    detail: (params) => ['quotes', 'detail', params],
  },
  payments: {
    all: () => ['payments'],
    byTask: (taskId) => ['payments', { taskId }],
  },

  // -- PJUD directory --
  // courts had two key shapes for the same fetch; unified to {page, pageSize}.
  courts: (params = { page: 1, pageSize: 100 }) => ['courts', params],
  tribunals: (params) => ['tribunals', params],

  // -- Notification actings (both call sites unify under one prefix) --
  notificationActings: {
    all: (caseId) => ['notificationActings', { caseId }],
    list: (params) => ['notificationActings', params],
  },

  // -- Org settings / billing --
  billingProfiles: {
    all: () => ['billingProfiles'],
    list: (params) =>
      params ? ['billingProfiles', params] : ['billingProfiles'],
  },
  bankAccounts: {
    all: () => ['bankAccounts'],
    list: (params) => ['bankAccounts', params],
  },
  orgProfile: {
    all: () => ['orgProfile'],
    detail: (params) => (params ? ['orgProfile', params] : ['orgProfile']),
  },
  members: (organizationId) => ['members', organizationId],
  dashboardInicio: (params) => ['dashboard-inicio', params],

  // -- Pagos (lawyer) --
  // One root: the Toku return invalidates every money view with one prefix
  // (a confirmed payin moves rows across cola/proceso/historial/comprobantes).
  pagos: {
    all: () => ['pagos'],
    cola: (params) => ['pagos', 'cola', params],
    proceso: (params) => ['pagos', 'proc', params],
    historial: (params) => ['pagos', 'hist', params],
    comprobantes: (params) => ['pagos', 'comprobantes', params],
  },

  // -- Pagos · Plataforma (backoffice payout queue) --
  // useLedgerQuery builds [key, { organizationId, ...params, pageSize }] from
  // the string prefix; `all()` is the matching invalidation prefix.
  payoutQueue: {
    key: 'plataforma-payout-queue',
    all: () => ['plataforma-payout-queue'],
  },

  // -- Cola · Plataforma (backoffice WhatsApp; API /channels/intakes) --
  channelIntakeQueue: {
    key: 'plataforma-channel-intake',
    all: () => ['plataforma-channel-intake'],
  },

  // -- Eventos · Plataforma (backoffice webhook deliveries) --
  // useLedgerQuery builds [key, { organizationId, ...params, pageSize }] from
  // the string prefix; `all()` is the matching invalidation prefix, which the
  // reprocess uses so the row shows its new status without a reload.
  webhookEvents: {
    key: 'plataforma-webhook-events',
    all: () => ['plataforma-webhook-events'],
  },

  // -- Eventos · Plataforma (unmatched provider money) --
  // Cross-org and unpaginated, so the key carries nothing but its own name. The
  // org still travels in the header, and it does not change the answer.
  unmatchedPayments: () => ['plataforma-unmatched-payments'],
  offerLinkTarget: () => ['plataforma-offer-link-target'],

  // -- Documentos · Plataforma (facturas by folio) --
  // Cross-org: the folio is the whole question. `all()` is what an annulment
  // invalidates, so the card reads the new state of the factura.
  taxDocumentsByFolio: {
    all: () => ['plataforma-tax-documents'],
    byFolio: (folio) => ['plataforma-tax-documents', folio],
  },

  // -- Misc --
  // Org-scoped: pricing is per-organization (tier depends on its 12m volume);
  // an unscoped key would leak one org's cached tier into another on switch.
  pricing: (organizationId) => ['pricing-calculate', organizationId ?? null],
  // The canonical tier table is org-agnostic (public endpoint).
  pricingTiers: () => ['pricing-tiers'],
  // Org inside the key: an org switch must never serve the other org's usage.
  pricingUsage: (params) => ['pricing-usage', params],

  // -- Command palette (⌘K) --
  // Deliberately outside qk.cases and qk.tasks: invalidating the case or
  // diligence lists must never refetch palette results, and a palette
  // search must never refetch the lists.
  commandPalette: {
    causas: (organizationId, term) => [
      'commandPalette',
      'causas',
      organizationId,
      term,
    ],
    diligencias: (organizationId, term) => [
      'commandPalette',
      'diligencias',
      organizationId,
      term,
    ],
  },
};
